"use client";

import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import { createLogger } from "@/lib/logger";
import { useAuth } from "@/hooks/useAuth";
import { useUserMeQuery } from "@/hooks/useUserMeQuery";
import { batchFetch } from "@/hooks/useBatchAPI";
import { useCurrentSource } from "@/hooks/useCurrentSource";
import { sourceUrl } from "@/lib/source-url";
import { authFetch } from "@/lib/authFetch";
import { useSidebar } from "@/contexts/SidebarContext";
import { useT } from "@/hooks/useT";

const logger = createLogger("UsersGridWrapper");
import UsersFilters, { TradersTimeWindowPills } from "./UsersFilters";
import UsersGrid from "./UsersGrid";
import { type PolymarketTrader } from "@/lib/polymarket";
import { normalizeTrader } from "../utils/normalizeTrader";
import { loadWatchlistCache, saveWatchlistCache, loadWatchlistAddressesCache, saveWatchlistAddressesCache } from "@/lib/watchlist-cache";
import AdvancedFiltersPanel, { type AdvancedFilters } from "./AdvancedFiltersDrawer";

// Lazy-load CompareTraders - only needed when comparing traders
const CompareTraders = dynamic(() => import("./CompareTraders"), {
  ssr: false,
});

// Lazy-load CopyTraderModal - only needed when user clicks to copy
const CopyTraderModal = dynamic(() => import("./CopyTraderModal"), {
  ssr: false,
});

const ITEMS_PER_PAGE = 20;

// Predexon Pro plan outage (unpaid quota). When true we hide every UI/data
// surface that depends on the PAID leaderboard tier (advanced filters, ROI/
// win-rate/profit-factor sorts, HFT detection, and the time-window pills — all
// route through the windowed Predexon path that returns zeroed columns while
// Pro is down). The grid then falls back to the all-time MV/DB path, which is
// sourced from Polymarket's FREE data-api and carries the full column set.
// Flip back to false (env) when the Pro quota is restored.
const HIDE_PREDEXON = process.env.NEXT_PUBLIC_HIDE_PREDEXON_TABS === "true";

// Skeleton loader component for traders
function TradersSkeletonLoader({ viewMode }: { viewMode: "grid" | "list" }) {
  const t = useT();
  if (viewMode === "list") {
    return (
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-white/[0.04] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-white/[0.06] border-b border-gray-200 dark:border-white/[0.04]">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-sans font-medium text-gray-500 dark:text-gray-400">{t("traders.col.trader")}</th>
                <th className="px-3 py-3 text-right text-xs font-sans font-medium text-gray-500 dark:text-gray-400">{t("traders.col.pnl")}</th>
                <th className="px-3 py-3 text-right text-xs font-sans font-medium text-gray-500 dark:text-gray-400 hidden sm:table-cell">{t("traders.col.volume")}</th>
                <th className="px-3 py-3 text-right text-xs font-sans font-medium text-gray-500 dark:text-gray-400 hidden md:table-cell">{t("traders.col.balance")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-[#1A1A1A]">
              {Array.from({ length: 10 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-gray-200 dark:bg-white/[0.06] rounded-[5px]" />
                      <div className="w-10 h-10 bg-gray-200 dark:bg-white/[0.06] rounded-[5px]" />
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 dark:bg-white/[0.06] rounded w-24 mb-1" />
                        <div className="h-3 bg-gray-200 dark:bg-white/[0.06] rounded w-16" />
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="h-4 bg-gray-200 dark:bg-white/[0.06] rounded w-16 ml-auto" />
                  </td>
                  <td className="px-3 py-3 text-right hidden sm:table-cell">
                    <div className="h-4 bg-gray-200 dark:bg-white/[0.06] rounded w-14 ml-auto" />
                  </td>
                  <td className="px-3 py-3 text-right hidden md:table-cell">
                    <div className="h-4 bg-gray-200 dark:bg-white/[0.06] rounded w-16 ml-auto" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Grid view skeleton - Same design on all devices
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 lg:gap-4">
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="bg-white dark:bg-[#18181D] border border-gray-200 dark:border-white/[0.04] rounded-[5px] overflow-hidden animate-pulse">
          {/* Header */}
          <div className="p-3 pb-2">
            <div className="flex items-start gap-2.5">
              <div className="w-11 h-11 bg-gray-200 dark:bg-white/[0.06] rounded-[5px]" />
              <div className="flex-1">
                <div className="h-4 bg-gray-200 dark:bg-white/[0.06] rounded w-24 mb-1.5" />
                <div className="h-3 bg-gray-200 dark:bg-white/[0.06] rounded w-16" />
              </div>
              <div className="w-8 h-8 bg-gray-200 dark:bg-white/[0.06] rounded-[5px]" />
            </div>
          </div>
          {/* PnL section */}
          <div className="px-3 pb-2">
            <div className="p-2 bg-gray-100 dark:bg-white/[0.06] rounded-[5px]">
              <div className="h-3 bg-gray-200 dark:bg-[#0a0a0b] rounded w-8 mb-1" />
              <div className="h-5 bg-gray-200 dark:bg-[#0a0a0b] rounded w-20" />
            </div>
          </div>
          {/* Bottom stats */}
          <div className="px-3 pb-3">
            <div className="grid grid-cols-3 gap-1.5">
              {[1, 2, 3].map((j) => (
                <div key={j} className="bg-gray-100 dark:bg-white/[0.06] rounded-[5px] p-2">
                  <div className="h-2 bg-gray-200 dark:bg-[#0a0a0b] rounded w-8 mx-auto mb-1" />
                  <div className="h-3 bg-gray-200 dark:bg-[#0a0a0b] rounded w-12 mx-auto" />
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

interface UsersGridWrapperProps {
  initialTraders?: PolymarketTrader[];
  initialTotal?: number;
  initialSource?: string;
}

export default function UsersGridWrapper({ initialTraders, initialTotal, initialSource }: UsersGridWrapperProps) {
  const t = useT();
  const { isLoading, isReady, isLoggedIn, currentUserId, currentWalletAddress } = useAuth();
  // Pull the FrenFlow internal user UUID from /api/users/me — that's
  // what the backend gates the X-only filter against. Privy's user.id
  // is a `did:privy:xxx` string, NOT this UUID; emails are unreliable
  // on Google OAuth. /me is the only authoritative source the client
  // has for the same identifier the backend uses.
  const userMe = useUserMeQuery();
  const { isCollapsed } = useSidebar();
  const searchParams = useSearchParams();
  const router = useRouter();
  const currentSource = useCurrentSource();
  // Client hook (localStorage, URL) is authoritative after hydration.
  // Server-provided initialSource is only used as fallback before client hook resolves.
  // Kalshi traders surface was removed (no copytrading on Kalshi). useCurrentSource
  // can still return 'kalshi' from a stale localStorage frenflow_source (set while
  // browsing Kalshi markets, which we keep) or a bookmarked ?source=kalshi — that must
  // NOT drive the grid into the now-dead Kalshi path. Coerce it back to the page's own
  // source (Polymarket on /traders).
  const rawSource = currentSource || initialSource || 'polymarket';
  const effectiveSource = rawSource === 'kalshi' ? (initialSource || 'polymarket') : rawSource;
  const [traders, setTraders] = useState<PolymarketTrader[]>(initialTraders || []);
  // 2026-05-26: ref so the watchlist hydration effect can read the
  // current leaderboard pool WITHOUT rerunning every time `traders`
  // changes. The leaderboard refetches every few seconds; if `traders`
  // were a real dep the watchlist effect would re-fire on every poll,
  // calling setExtraWatchlistTraders with a new array and forcing the
  // grid to re-render → visible flash on every refresh.
  const tradersRef = useRef<PolymarketTrader[]>(traders);
  useEffect(() => { tradersRef.current = traders; }, [traders]);
  const [totalTraders, setTotalTraders] = useState(initialTotal || 0);
  const [watchlistAddresses, setWatchlistAddresses] = useState<string[]>([]);
  const [initialLoading, setInitialLoading] = useState(!initialTraders || initialTraders.length === 0);
  const [refetching, setRefetching] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");
  // "Show HFT bots" toggle. OFF (default) hides HFT from the general list. ON
  // shows the MIXED list (normals + HFT together), NOT only-HFT.
  const [showHft, setShowHft] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState("all");
  const [sortBy, setSortBy] = useState(() => {
    const urlSort = searchParams.get("sort");
    if (urlSort) return urlSort;
    // Default sort = PnL for all sources (2026-06-15): for "who do I copy",
    // ranking by money-made beats volume (activity ≠ quality — top volume is
    // often churning market-makers). 'rank' (=volume) stays one click away.
    return 'pnl';
  });
  // Default window. Polymarket → '30d': for a user deciding WHO to copy, 30d
  // is the sweet spot — enough sample to be meaningful, recent enough to show
  // current form (1d = noise, all-time = stale glory). Kalshi has no 30d
  // window so it stays 'all_time'. URL ?time= always wins.
  const [timePeriod, setTimePeriod] = useState<string>(() => {
    const urlTime = searchParams.get("time");
    if (urlTime) return urlTime;
    // Under HIDE_PREDEXON default to all_time so the user lands on the FULL
    // free column set (all-time MV path). The 1d/7d/30d pills still work but
    // only carry PnL+Volume, so all-time is the richer default.
    return (initialSource === 'polymarket' && !HIDE_PREDEXON) ? '30d' : 'all_time';
  });

  // X-only filter for the founder allow-list. Gated by the FrenFlow
  // internal UUID from /api/users/me, which is what the backend uses
  // — same identifier on both sides means the toggle visibility and
  // the actual filter authorization are guaranteed to agree. The
  // backend ALSO enforces the gate (silently strips ?requireX=true
  // for non-allowed users + sets private cache headers) so the
  // curated view never leaks even if someone hand-crafts the URL.
  const X_FILTER_ALLOWED_USER_IDS = new Set([
    '4db36ba5-2168-4a39-b7a2-110f4229474b', // frenflowdev
    '888fd147-f1d8-4c13-b3f6-20cd4bc2e98d', // marcnietobrand
    '97a93cdb-dc0e-4908-94d9-5ddea2075c9c', // futureoddstv
    '20a90bde-83e4-4422-8e8a-32ee92152784', // zeroxitadori
  ]);
  const myFrenFlowId = (userMe.data?.id as string | undefined) ?? null;
  // Dev bypass: in local development show the toggle without needing an
  // allow-listed login (matches the backend, which honours ?requireX in
  // non-production). NODE_ENV is inlined at build → always 'production' on
  // frenflow.com, so the founder gate still applies in prod.
  const xFilterDevBypass = process.env.NODE_ENV !== 'production';
  const canToggleXFilter = effectiveSource === 'polymarket'
    && (xFilterDevBypass || (!!myFrenFlowId && X_FILTER_ALLOWED_USER_IDS.has(myFrenFlowId)));

  // Toggle state — sticky in localStorage so it survives navigations.
  const [xFilterEnabled, setXFilterEnabled] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!canToggleXFilter) {
      setXFilterEnabled(false);
      return;
    }
    setXFilterEnabled(window.localStorage.getItem('traders:xFilter') === '1');
  }, [canToggleXFilter]);
  const handleToggleXFilter = useCallback(() => {
    setXFilterEnabled((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('traders:xFilter', next ? '1' : '0');
      }
      return next;
    });
  }, []);
  const requireXTraders = canToggleXFilter && xFilterEnabled;

  // Cache Kalshi tab results to avoid flickering on tab switch
  const kalshiTabCacheRef = useRef<Record<string, { traders: PolymarketTrader[]; total: number }>>({});
  const [sparklines, setSparklines] = useState<Record<string, number[]>>({});
  // Default = 'grid' (2026-06-15): the visual discovery cards are the first
  // impression for a new user deciding who to copy. Initial state MUST match
  // the server-rendered HTML (SSR has no localStorage, so it renders this
  // default). A returning user who saved 'list' is flipped in the layout
  // effect below (runs before paint), accepting a one-frame skeleton for them
  // but NOT for the common new-user case (who matches the SSR 'grid').
  const [viewMode, setViewModeState] = useState<"grid" | "list">("grid");
  useLayoutEffect(() => {
    // Detect SSR↔client state mismatches and switch to skeleton until the
    // client resolves. Two sources of mismatch:
    //   1. viewMode: SSR renders the default ('grid'); a returning user who
    //      saved 'list' (or a ?view= deep link) differs.
    //   2. timePeriod: SSR fetched the source default (30d PM / all_time);
    //      a ?time= deep link differs.
    // Without this, such users see the SSR version for ~200ms before it flips.
    // Setting initialLoading=true here makes the skeleton take over before
    // paint; the fetch effect resets it after fresh data lands.
    let mismatch = false;

    // ?view=grid|list in the URL wins over localStorage (deep links).
    const urlView = new URLSearchParams(window.location.search).get("view");
    if (urlView === "grid" || urlView === "list") {
      if (urlView !== viewMode) {
        setViewModeState(urlView);
        mismatch = true;
      }
    } else {
      const stored = localStorage.getItem('traders_view_mode');
      if ((stored === "grid" || stored === "list") && stored !== viewMode) {
        setViewModeState(stored);
        mismatch = true;
      }
    }

    // timePeriod: SSR fetched the source default ('30d' for Polymarket,
    // 'all_time' otherwise). Only flag mismatch when the URL pins a different
    // window, since then the client refetches for the override.
    const defaultWindow = (initialSource === 'polymarket' && !HIDE_PREDEXON) ? '30d' : 'all_time';
    const urlTime = new URLSearchParams(window.location.search).get("time");
    if (urlTime && urlTime !== defaultWindow) {
      mismatch = true;
    }

    if (mismatch) setInitialLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const setViewMode = (mode: "grid" | "list") => {
    setViewModeState(mode);
    if (typeof window !== 'undefined') localStorage.setItem('traders_view_mode', mode);
    syncUrlParam("view", mode === "list" ? null : mode); // list is default, only persist grid
  };
  const [currentPage, setCurrentPageState] = useState(() => {
    const p = parseInt(searchParams.get("page") || "1", 10);
    return isNaN(p) || p < 1 ? 1 : p;
  });
  const setCurrentPage = (v: number | ((p: number) => number)) => {
    setCurrentPageState(prev => {
      const next = typeof v === 'function' ? v(prev) : v;
      // URL sync moved out of the updater into a microtask. React 19 /
      // Next 16 flags DOM mutations (history.replaceState) inside a
      // state updater as setState-in-render because updaters can re-run
      // during concurrent render passes — observable side effect like
      // replaceState would fire twice. Deferring to queueMicrotask
      // keeps the URL in sync without coupling it to the render cycle.
      if (next !== prev) {
        queueMicrotask(() => {
          const url = new URL(window.location.href);
          if (next > 1) url.searchParams.set('page', String(next));
          else url.searchParams.delete('page');
          window.history.replaceState({}, '', url.toString());
        });
      }
      return next;
    });
  };
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const advancedFilterCount = Object.values(advancedFilters).filter(v => v !== undefined && v !== "").length;
  const [compareTraders, setCompareTraders] = useState<PolymarketTrader[]>([]);
  const [copyModal, setCopyModal] = useState<{
    isOpen: boolean;
    trader: PolymarketTrader | null;
  }>({ isOpen: false, trader: null });

  // Sync filter from URL after hydration
  useEffect(() => {
    const urlFilter = searchParams.get("filter");
    if (urlFilter && urlFilter !== selectedFilter) {
      setSelectedFilter(urlFilter);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Extra traders fetched for watchlist addresses not in the leaderboard
  const [extraWatchlistTraders, setExtraWatchlistTraders] = useState<PolymarketTrader[]>([]);

  // Helper: sync a single URL param without full router navigation
  const syncUrlParam = useCallback((key: string, value: string | null) => {
    const url = new URL(window.location.href);
    if (value) {
      url.searchParams.set(key, value);
    } else {
      url.searchParams.delete(key);
    }
    window.history.replaceState({}, '', url.toString());
  }, []);

  // Sync filter to URL
  const handleSetFilter = useCallback((filter: string) => {
    setSelectedFilter(filter);
    setCurrentPage(1);
    syncUrlParam("filter", filter === "all" ? null : filter);
  }, [syncUrlParam]);

  // Wrap setSortBy to also sync to URL
  const defaultSort = 'pnl';
  const handleSetSort = useCallback((sort: string) => {
    setSortBy(sort);
    setCurrentPage(1);
    syncUrlParam("sort", sort === defaultSort ? null : sort);
  }, [syncUrlParam, defaultSort]);

  // Reset to page 1 when the X-only filter is toggled — otherwise the
  // user would be on a deep page that probably doesn't exist anymore
  // after the result set shrinks/grows.
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xFilterEnabled]);

  // Track if initial batch load has been done
  const initialLoadDoneRef = useRef(false);
  // True once the watchlist (favorites) list has been fetched at least
  // once — flipped in EVERY path that calls /api/tracker/wallets (success
  // OR failure). Gates the auto-bounce-to-"all" effect below so a hard
  // refresh of /traders?filter=watchlist doesn't strip the filter
  // during the brief window between mount and the wallets-list fetch.
  const watchlistFetchedRef = useRef(false);

  // Grid view is now the default on all devices
  // (removed mobile list view override per user request)

  // Watchlist fetch for subsequent loads (after initial)
  useEffect(() => {
    // Skip if initial load will handle this via batch
    if (!initialLoadDoneRef.current) return;

    if (isLoggedIn) {
      authFetch(`/api/tracker/wallets?source=${effectiveSource}`)
        .then((res) => res.json())
        .then((data) => {
          const wallets = data.wallets || [];
          setWatchlistAddresses(wallets.map((w: { address: string }) => w.address.toLowerCase()));
        })
        .catch((err) => logger.error("Error fetching watchlist", { error: err instanceof Error ? err.message : String(err) }))
        .finally(() => { watchlistFetchedRef.current = true; });
    } else {
      // Not logged in → no fetch will happen, so mark fetched=true
      // immediately so the bounce gate doesn't keep us pinned.
      watchlistFetchedRef.current = true;
    }
    // effectiveSource: favorites are per-source, so refetch when the active
    // platform changes (PM ↔ Predict) to swap in that source's favorites.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, effectiveSource]);

  // 2026-05-27: hydrate watchlistAddresses from localStorage as soon as
  // currentUserId resolves, BEFORE /api/tracker/wallets returns. Without
  // this, /traders?filter=watchlist re-visits paint placeholder rows
  // ("0x17e0…fe5b" without name/avatar) for the ~200-500ms between
  // mount and the wallets fetch — the trader-profile cache then upgrades
  // them visibly, producing the flicker @opicazom flagged on 2026-05-27.
  // With both caches warm: addresses + profiles render on first paint, the
  // network just refreshes silently in the background.
  useEffect(() => {
    if (!currentUserId) return;
    const cached = loadWatchlistAddressesCache(currentUserId);
    if (cached.length === 0) return;
    setWatchlistAddresses((prev) => (prev.length === 0 ? cached : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  // Mirror watchlistAddresses to cache on every change (initial fetch,
  // optimistic star toggle, batch refresh). Keeps the next-visit hydration
  // in sync with the server state without touching each setWatchlistAddresses
  // call site.
  useEffect(() => {
    if (!currentUserId) return;
    if (!watchlistFetchedRef.current) return; // don't persist the seeded cache back over itself
    saveWatchlistAddressesCache(currentUserId, watchlistAddresses);
  }, [currentUserId, watchlistAddresses]);

  // Fetch traders with server-side pagination, sort, filter, and search
  const loadTraders = useCallback(async (page: number, search?: string) => {
    // Refetch indicator (timePeriod / sort / filter / pagination
    // changes). Keeps the existing skeleton showing while Predexon
    // round-trips, instead of freezing on stale rows for 1-2 s.
    // Separate state from initialLoading so the load-on-deps effect's
    // `if (initialLoading) return` guard isn't tripped mid-refetch.
    // Search has its own searching-state, so skip the full skeleton.
    if (!search) setRefetching(true);
    const offset = (page - 1) * ITEMS_PER_PAGE;
    const apiSortBy = sortBy === 'rank' ? 'volume' : sortBy;
    const cacheKey = effectiveSource === 'kalshi' ? `${apiSortBy}:${timePeriod}:${page}` : '';

    // Kalshi tab cache: restore instantly to avoid flickering
    if (cacheKey && kalshiTabCacheRef.current[cacheKey] && !search) {
      const cached = kalshiTabCacheRef.current[cacheKey];
      setTraders(cached.traders);
      setTotalTraders(cached.total);
      setInitialLoading(false);
      setSearching(false);
      setRefetching(false);
      // Still refresh in background (don't await, no loading state)
      authFetch(`/api/traders/leaderboard?limit=${ITEMS_PER_PAGE}&offset=${offset}&sortBy=${apiSortBy}&source=kalshi${timePeriod !== 'all_time' ? `&timePeriod=${timePeriod}` : ''}`)
        .then(r => r.json())
        .then(data => {
          const arr = Array.isArray(data) ? data : (data.traders || []);
          const normalized = arr.map(normalizeTrader);
          kalshiTabCacheRef.current[cacheKey] = { traders: normalized, total: data.total || arr.length };
          setTraders(normalized);
          setTotalTraders(data.total || arr.length);
        })
        .catch(() => {}); // silent background refresh
      return;
    }

    try {
      let url = `/api/traders/leaderboard?limit=${ITEMS_PER_PAGE}&offset=${offset}&sortBy=${apiSortBy}`;
      if (effectiveSource === 'kalshi') {
        url += '&source=kalshi';
        if (timePeriod && timePeriod !== 'all_time') {
          url += `&timePeriod=${timePeriod}`;
        }
      } else if (effectiveSource === 'predict') {
        url += '&source=predict';
      }
      if (selectedFilter && selectedFilter !== 'all' && selectedFilter !== 'watchlist') {
        // Map quick-filter pills to real Predexon API params
        const styleFilterMap: Record<string, string> = {
          whales: 'WHALE',
          contrarian: 'CONTRARIAN',
          value: 'VALUE_HUNTER',
          conviction: 'HIGH_CONVICTION',
          degen: 'DEGEN',
          'market-maker': 'MARKET_MAKER',
        };
        const metricPresets: Record<string, string> = {
          profitable: 'min_total_pnl=1',
          'high-roi': 'min_roi=0.5',
          sharp: 'min_profit_factor=2',
        };
        const mappedStyle = styleFilterMap[selectedFilter];
        if (mappedStyle) {
          url += `&style=${mappedStyle}`;
        } else if (metricPresets[selectedFilter]) {
          url += `&${metricPresets[selectedFilter]}`;
        } else if (selectedFilter === 'hft') {
          // FrenFlow-side filter — backend keeps only rows where the
          // enriched `isHft===true` flag is set (Redis `hft:addresses`
          // OR computed tradesPerHour > 200). Forces Predexon routing
          // server-side because we need the enriched flag.
          url += `&onlyHft=true`;
        }
      }
      // Hide HFT bots from the GENERAL list by default (they aren't copyable
      // and crown the PnL ranking). The "Show HFT bots" toggle, when ON, drops
      // this filter so the list is MIXED (normals + HFT), not only-HFT.
      // 'watchlist' always shows whatever the user saved.
      // excludeHft and window both force the PAID Predexon leaderboard path.
      // While Pro is down (HIDE_PREDEXON) that path returns zeroed
      // trades/balance/bigwin, so we skip BOTH and let the request fall to the
      // all-time MV/DB path (full free column set). HFT exclusion is a no-op
      // during the outage anyway (its flag is Predexon-derived).
      if (effectiveSource === 'polymarket' && !showHft && !HIDE_PREDEXON && selectedFilter !== 'watchlist') {
        url += '&excludeHft=true';
      }
      // Add time window for Polymarket (Predexon window= param). Always
      // send it — including all_time — so the backend routes the request
      // to Predexon (which has correct lifetime stats) instead of falling
      // through to the legacy DB code path (which has stale snapshot data).
      // Under HIDE_PREDEXON only the 1d/7d/30d pills send a window (free PM
      // pm-fallback: PnL+Volume windowed, other cols hidden by the grid). The
      // all_time pill skips it → all-time MV path with the full free column set.
      if (effectiveSource === 'polymarket' && timePeriod && !(HIDE_PREDEXON && timePeriod === 'all_time')) {
        url += `&window=${timePeriod}`;
      }
      if (search) {
        url += `&search=${encodeURIComponent(search.replace(/^@/, ''))}`;
      }
      // Append advanced Predexon filters
      for (const [k, v] of Object.entries(advancedFilters)) {
        if (v !== undefined && v !== '') url += `&${k}=${encodeURIComponent(v)}`;
      }
      // Auto-curated leaderboard for founder/partner accounts: only
      // traders with a linked X account. See X_FILTER_AUTO_USER_IDS
      // above for the allow-list.
      if (requireXTraders) {
        url += '&requireX=true';
      }

      // Defensive fetch: deep-link pages with high offsets occasionally hit
      // proxy timeouts and Vercel returns HTML, which used to crash JSON.parse.
      // Validate, then retry once after a short backoff before surfacing.
      const fetchPage = async () => {
        const r = await authFetch(url);
        const ct = r.headers.get("content-type") || "";
        if (!r.ok || !ct.includes("application/json")) return null;
        return r.json();
      };

      let data = await fetchPage();
      if (!data) {
        await new Promise((res) => setTimeout(res, 800));
        data = await fetchPage();
      }
      if (!data) throw new Error("Leaderboard request returned a non-JSON response");

      const tradersArray = Array.isArray(data) ? data : (data.traders || []);
      const total = data.total || tradersArray.length;

      const normalizedTraders = tradersArray.map(normalizeTrader);

      // Cache Kalshi results by tab
      if (cacheKey && !search) {
        kalshiTabCacheRef.current[cacheKey] = { traders: normalizedTraders, total };
      }

      setTraders(normalizedTraders);
      setTotalTraders(total);
    } catch (error) {
      logger.error("Error loading traders", { error: error instanceof Error ? error.message : String(error) });
      setTraders([]);
      setTotalTraders(0);
    }
    setInitialLoading(false);
    setSearching(false);
    setRefetching(false);
  }, [sortBy, selectedFilter, effectiveSource, timePeriod, advancedFilters, requireXTraders, showHft]);

  // Initial load with batch API optimization
  useEffect(() => {
    if (initialLoadDoneRef.current) return;
    const hasSSRData = initialTraders && initialTraders.length > 0;

    const loadInitialData = async () => {
      // If user is logged in, batch traders + watchlist in single request
      if (isLoggedIn) {
        if (hasSSRData && currentPage === 1) {
          // Already have SSR traders for page 1 — just fetch watchlist
          try {
            const res = await authFetch(`/api/tracker/wallets?source=${effectiveSource}`);
            const data = await res.json();
            const wallets = data.wallets || [];
            setWatchlistAddresses(wallets.map((w: { address: string }) => w.address.toLowerCase()));
          } catch {
            // Ignore watchlist error
          }
          watchlistFetchedRef.current = true;
          initialLoadDoneRef.current = true;
          setInitialLoading(false);
        } else {
          const initialOffset = (currentPage - 1) * ITEMS_PER_PAGE;
          const requireXSuffix = requireXTraders ? '&requireX=true' : '';
          const tradersPath = effectiveSource !== 'polymarket'
            ? `/api/portfolio/traders?limit=${ITEMS_PER_PAGE}&offset=${initialOffset}&source=${effectiveSource}`
            : `/api/portfolio/traders?limit=${ITEMS_PER_PAGE}&offset=${initialOffset}${requireXSuffix}`;
          const result = await batchFetch<{
            traders: { traders: any[]; total: number };
            watchlist: { wallets: Array<{ address: string }> };
          }>([
            { id: "traders", path: tradersPath },
            { id: "watchlist", path: `/api/tracker/wallets?source=${effectiveSource}` },
          ]);

          if (result.data) {
            // Process traders
            const tradersData = result.data.traders;
            const tradersArray = Array.isArray(tradersData) ? tradersData : (tradersData?.traders || []);
            const total = tradersData?.total || tradersArray.length;
            setTraders(tradersArray.map(normalizeTrader));
            setTotalTraders(total);

            // Process watchlist
            const wallets = result.data.watchlist?.wallets || [];
            setWatchlistAddresses(wallets.map((w) => w.address.toLowerCase()));
          } else {
            // Fallback to single request if batch fails
            loadTraders(currentPage);
          }
          watchlistFetchedRef.current = true;
          initialLoadDoneRef.current = true;
          // Suppress the reload-on-deps effect's first fire after this
          // batch fetch has already populated `traders`. Without this,
          // when there's no SSR data the reloadMountSkipRef was init'd
          // to `false`, so as soon as initialLoading flips false the
          // reload effect (line ~558) fires loadTraders AGAIN. That
          // second fetch returns the same data with a fresh array ref,
          // so sparklinesAddressKey changes, the sparkline fetch
          // refires, and the grid renders twice in a row — the "2x
          // flicker on refresh" reported 2026-05-18.
          reloadMountSkipRef.current = true;
          setInitialLoading(false);
        }
      } else if (isReady) {
        if (hasSSRData && currentPage === 1) {
          // Already have SSR data for page 1, mark as done
          initialLoadDoneRef.current = true;
          watchlistFetchedRef.current = true;
          setInitialLoading(false);
        } else {
          // Load correct page (SSR only has page 1). Same skip-next-fire
          // contract as the logged-in batch path above.
          loadTraders(currentPage);
          initialLoadDoneRef.current = true;
          watchlistFetchedRef.current = true;
          reloadMountSkipRef.current = true;
        }
      }
    };

    loadInitialData();
  }, [isLoggedIn, isReady, loadTraders]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset everything when source changes
  const prevSourceRef = useRef(effectiveSource);
  useEffect(() => {
    if (prevSourceRef.current !== effectiveSource) {
      prevSourceRef.current = effectiveSource;
      setCurrentPage(1);
      setSearchTerm("");
      setSelectedFilter("all");
      setTimePeriod(effectiveSource === 'polymarket' && !HIDE_PREDEXON ? '30d' : 'all_time');
      setAdvancedFilters({});
      setSortBy('pnl');
      kalshiTabCacheRef.current = {}; // clear tab cache on source switch
    }
  }, [effectiveSource]);

  // Reload on page change or search change.
  // Mount-skip: when SSR pre-rendered page 1 traders, the initial-load
  // effect resolves without calling loadTraders. Without this guard,
  // *this* effect still fires on mount and triggers a redundant refetch
  // → grid flickers from SSR data → skeleton → fresh data (visually
  // identical 95% of the time). Skip the first fire when SSR data is
  // present AND we're on page 1 with no search term.
  const reloadMountSkipRef = useRef(
    !!(initialTraders && initialTraders.length > 0) // has SSR data
  );
  useEffect(() => {
    if (initialLoading) return;
    if (reloadMountSkipRef.current) {
      reloadMountSkipRef.current = false;
      return;
    }

    const debounce = setTimeout(() => {
      if (searchTerm) {
        setSearching(true);
      }
      loadTraders(currentPage, searchTerm || undefined);
    }, searchTerm ? 400 : 0);

    return () => clearTimeout(debounce);
  }, [currentPage, searchTerm, loadTraders, initialLoading]);

  // Fetch sparkline data for visible traders (fire-and-forget).
  // Derive a stable "addresses key" from the first 20 traders so the
  // effect only refires when the actual address set changes. Without
  // this, any `traders` state update (sort flip, ref change from a
  // refetch returning identical data) re-triggers sparklines → grid
  // re-render → flicker. Debounced 150ms to coalesce back-to-back
  // updates (e.g. setTraders + setTotalTraders in the same tick).
  // Sparklines only for the Leaderboard tab. Recommended hides the chart
  // (hideChart) — it's not copy-adjusted and ignores the timeframe pills — so
  // we don't fetch them there.
  const sparklinesAddressKey = useMemo(() => {
    if (traders.length === 0 || effectiveSource === 'kalshi') return '';
    return traders.slice(0, 20).map(t => t.address).filter(Boolean).join(',');
  }, [traders, effectiveSource]);

  useEffect(() => {
    if (!sparklinesAddressKey) return;
    const timer = setTimeout(() => {
      fetch(`/api/traders/sparklines?addresses=${encodeURIComponent(sparklinesAddressKey)}&source=${effectiveSource}`)
        .then(r => r.ok ? r.json() : { sparklines: {} })
        .then(data => {
          if (data.sparklines && Object.keys(data.sparklines).length > 0) {
            setSparklines(prev => ({ ...prev, ...data.sparklines }));
          }
        })
        .catch(() => {});
    }, 150);
    return () => clearTimeout(timer);
  }, [sparklinesAddressKey, effectiveSource]);

  // Fetch ALL watchlist traders when watchlist filter is active
  // Fetches tracker wallets directly, then enriches each with trader profile
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  useEffect(() => {
    if (selectedFilter !== "watchlist" || !isLoggedIn) {
      setExtraWatchlistTraders([]);
      return;
    }

    // 2026-05-20: drive the favorites grid from the LOCAL
    // watchlistAddresses state, not from a fresh /api/tracker/wallets
    // round-trip. The old flow created a race: the star button does an
    // optimistic setWatchlistAddresses(+1) immediately, then POSTs
    // /api/watchlist async. This effect (triggered by the length change)
    // would race the POST and call GET /api/tracker/wallets BEFORE the
    // POST committed, get back the stale list (length=0), and overwrite
    // the optimistic state — making the new fav vanish from the grid.
    //
    // Using watchlistAddresses as the source of truth here means:
    //   • New favs appear immediately (no waiting on the POST round-trip).
    //   • Removed favs disappear immediately.
    //   • We only fetch trader profile metadata, not the wallet list.
    //   • The server stays the source of truth across sessions (initial
    //     watchlistAddresses come from /api/tracker/wallets on mount).
    const addresses = watchlistAddresses;
    if (addresses.length === 0) {
      setExtraWatchlistTraders([]);
      setWatchlistLoading(false);
      return;
    }

    // 2026-05-20: cache previously-loaded watchlist traders so toggling
    // the filter back doesn't refetch the same N profiles, and reuse
    // traders already in the leaderboard array (loaded for the main
    // list view) — those have full metadata, no enrichment needed.
    // Net result: clicking the Favorites pill shows the user's
    // existing traders INSTANTLY if any are already in `traders`, and
    // only the truly-new addresses incur a profile fetch.
    const knownByAddress = new Map<string, PolymarketTrader>();
    for (const tr of tradersRef.current) {
      if (tr?.address) knownByAddress.set(tr.address.toLowerCase(), tr);
    }
    for (const tr of extraWatchlistTraders) {
      if (!tr?.address) continue;
      // 2026-05-26: skip placeholder rows (username == truncated address)
      // so the fetch loop can upgrade them on the next effect run. Without
      // this, a re-fire of the effect (e.g. `traders` changed mid-flight)
      // cancels the original batch fetch via the cleanup `cancelled=true`,
      // and on the next run the placeholders are mistakenly counted as
      // "known" — missing.length=0 — so no new fetch ever starts and the
      // grid stays stuck on `0x17e0…fe5b` / 0 trades forever.
      const truncated = `${tr.address.slice(0, 6)}...${tr.address.slice(-4)}`;
      if (tr.username === truncated) continue;
      knownByAddress.set(tr.address.toLowerCase(), tr);
    }
    // 2026-05-26: hydrate from localStorage cache so re-visits paint
    // with real avatars/usernames instead of "0x17e0…fe5b" placeholders.
    // The network fetch still runs and overwrites these — cache is only
    // a paint-time hint. First-visit users still see the placeholder
    // flow until the fetch resolves.
    for (const tr of loadWatchlistCache(currentUserId)) {
      if (!tr?.address) continue;
      const lc = tr.address.toLowerCase();
      // Don't override fresher data already in knownByAddress from
      // `traders` or `extraWatchlistTraders` upgrades earlier this run.
      if (!knownByAddress.has(lc)) knownByAddress.set(lc, tr);
    }

    const missing: string[] = [];
    const resolved: PolymarketTrader[] = [];
    for (const address of addresses) {
      const cached = knownByAddress.get(address.toLowerCase());
      if (cached) {
        resolved.push(cached);
      } else {
        missing.push(address);
      }
    }

    // 2026-05-25 (fix C + A): progressive render via single batch call.
    //   - C: show ALL favs immediately as placeholders so the grid
    //     paints in <100ms regardless of how many favs the user has.
    //   - A: replace the N-parallel /api/traders/[addr] fan-out with a
    //     single POST /api/traders/batch — server dedupes enrichments
    //     across the whole set (1 HFT Redis read, 1 SQL query for the
    //     polymarket_users miss set, 1 SQL query for the FrenFlow join,
    //     parallel PM fetches only for addresses the DB doesn't know).
    //     Browser side: 1 round-trip instead of N, with HTTP/2 stream
    //     contention eliminated.
    const placeholderForMissing = missing.map((address) =>
      normalizeTrader({
        address,
        username: `${address.slice(0, 6)}...${address.slice(-4)}`,
      }),
    );
    const initialOrdered = (() => {
      const mergedByAddress = new Map<string, PolymarketTrader>();
      for (const tr of resolved) mergedByAddress.set(tr.address.toLowerCase(), tr);
      for (const tr of placeholderForMissing) mergedByAddress.set(tr.address.toLowerCase(), tr);
      return addresses
        .map((a) => mergedByAddress.get(a.toLowerCase()))
        .filter((t): t is PolymarketTrader => !!t);
    })();
    setExtraWatchlistTraders(initialOrdered);
    if (missing.length === 0) {
      setWatchlistLoading(false);
      return;
    }

    setWatchlistLoading(false);

    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch('/api/traders/batch', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ addresses: missing }),
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const fetched: PolymarketTrader[] = Array.isArray(data?.traders)
          ? data.traders.map((t: unknown) => normalizeTrader(t))
          : [];
        if (cancelled || fetched.length === 0) return;
        // Apply each upgrade in-place so React diffs are minimal and
        // the existing placeholder rows fade into real data without
        // a full grid re-mount.
        const upgradeByAddr = new Map<string, PolymarketTrader>();
        for (const tr of fetched) {
          if (tr?.address) upgradeByAddr.set(tr.address.toLowerCase(), tr);
        }
        setExtraWatchlistTraders((prev) => {
          const next = prev.map((t) => upgradeByAddr.get(t.address.toLowerCase()) ?? t);
          // 2026-05-26: persist every non-placeholder row so the next
          // visit hydrates instantly. A "placeholder" here is a row whose
          // username equals the address-truncated form (`0x17e0…fe5b`) —
          // those carry no identity worth re-painting with. Everything
          // else (real fetch result or hit from the leaderboard pool)
          // is fair game.
          const cacheable = next.filter((t) => {
            if (!t?.address) return false;
            const truncated = `${t.address.slice(0, 6)}...${t.address.slice(-4)}`;
            return t.username !== truncated;
          });
          if (cacheable.length > 0) saveWatchlistCache(currentUserId, cacheable);
          return next;
        });
      } catch (err) {
        logger.error("Failed to load watchlist traders (batch)", {
          missingCount: missing.length,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    })();
    return () => { cancelled = true; };
    // 2026-05-26: deliberately omit `traders` and `extraWatchlistTraders`
    // from deps — both are read via ref / closure for the merge logic
    // but we don't want the leaderboard polling (which mutates `traders`
    // every few seconds) to refire this effect. The hydration runs once
    // per filter-switch / login-change / watchlist-mutation, and the
    // localStorage cache covers re-visits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter, isLoggedIn, watchlistAddresses, currentUserId]);

  // 2026-05-20: when the user un-stars their last favorite while sitting
  // on the Favorites filter, fall back to "All" so they're not left on a
  // filter that's about to disappear from the bar (the pill hides itself
  // at watchlistCount=0 — see UsersFilters). Done in an effect so it
  // reacts to remove-via-star clicks anywhere on the page, including
  // the card inside the favorites grid that they just dismissed.
  //
  // 2026-05-26 regression fix: gate the bounce on watchlistFetchedRef
  // (see the watchlist fetch effect above) so a hard refresh of
  // /traders?filter=watchlist doesn't kick the user back to "all"
  // during the brief window between mount and the wallets-list fetch
  // resolving (state defaults to []). Without this gate, every refresh
  // strips the filter from the URL and re-routes to "all".
  useEffect(() => {
    if (!watchlistFetchedRef.current) return;
    if (selectedFilter === "watchlist" && watchlistAddresses.length === 0 && isLoggedIn) {
      handleSetFilter("all");
    }
  }, [watchlistAddresses.length, selectedFilter, isLoggedIn, handleSetFilter]);

  // Compare traders functions
  const handleAddToCompare = useCallback((trader: PolymarketTrader) => {
    setCompareTraders((prev) => {
      // Check if already added
      if (prev.some((t) => t.address === trader.address)) return prev;
      // Max 2 traders
      if (prev.length >= 2) return prev;
      return [...prev, trader];
    });
  }, []);

  const handleRemoveFromCompare = useCallback((address: string) => {
    setCompareTraders((prev) => prev.filter((t) => t.address !== address));
  }, []);

  const handleClearCompare = useCallback(() => {
    setCompareTraders([]);
  }, []);

  const handleCopyTrader = useCallback((trader: PolymarketTrader) => {
    setCopyModal({ isOpen: true, trader });
  }, []);

  // Memoize filtered traders
  // Sort/filter for non-watchlist is now server-side; only watchlist + market filter remain client-side
  const filteredTraders = useMemo(() => {
    // For watchlist: use dedicated fetched traders (client-side sort/filter)
    if (selectedFilter === "watchlist") {
      let result = [...extraWatchlistTraders];
      if (selectedMarket !== "all") {
        const marketName = selectedMarket.charAt(0).toUpperCase() + selectedMarket.slice(1);
        result = result.filter(t => t.marketCategories?.includes(marketName));
      }
      return result.sort((a, b) => {
        if (sortBy === "pnl") return (b.totalPnL || 0) - (a.totalPnL || 0);
        if (sortBy === "volume") return b.totalVolume - a.totalVolume;
        if (sortBy === "balance") return (b.currentBalance || 0) - (a.currentBalance || 0);
        if (sortBy === "bigwin") return (b.largestWin || 0) - (a.largestWin || 0);
        return (a.rank || 999) - (b.rank || 999);
      });
    }

    // Server-side sorted/filtered — only apply market filter client-side
    if (selectedMarket !== "all") {
      const marketName = selectedMarket.charAt(0).toUpperCase() + selectedMarket.slice(1);
      return traders.filter(t => t.marketCategories?.includes(marketName));
    }

    return traders;
  }, [traders, selectedFilter, selectedMarket, sortBy, extraWatchlistTraders]);

  // Reset to page 1 when search changes — skip the mount fire so a deep link
  // like /traders?page=10 keeps its initial page instead of being clobbered.
  const searchTermInitRef = useRef(true);
  useEffect(() => {
    if (searchTermInitRef.current) {
      searchTermInitRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [searchTerm]);

  // Calculate pagination using server-side total
  const totalPages = Math.ceil(totalTraders / ITEMS_PER_PAGE);

  // Time-window pills. Hidden entirely under HIDE_PREDEXON: the windowed path
  // needs Predexon Pro (down) and returns zeroed trades/balance/bigwin, so we
  // drop the pills and serve the all-time free column set instead.
  const renderTimePills = (inline: boolean) => (
    <TradersTimeWindowPills
      inline={inline}
      timePeriod={timePeriod}
      setTimePeriod={(tp) => {
        setTimePeriod(tp);
        setCurrentPage(1);
        const implicitDefault = (effectiveSource === 'polymarket' && !HIDE_PREDEXON) ? '1d' : 'all_time';
        syncUrlParam("time", tp === implicitDefault ? null : tp);
      }}
      source={effectiveSource}
      hftActive={showHft}
      // HFT detection depends on Predexon (refreshHftAddressCache) — frozen
      // during the outage, so hide the "Show HFT bots" toggle too (flag-gated).
      onToggleHft={effectiveSource === 'polymarket' && !HIDE_PREDEXON
        ? () => { setShowHft((v) => !v); setCurrentPage(1); }
        : undefined}
    />
  );
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;

  return (
    <div className="min-h-screen">
      {/* Sort + category filters hoisted above the grid (2026-06-18, user
          request). */}
      {(
        <div className="mobile-cat-bar sticky top-12 z-[60] backdrop-blur-[20px] backdrop-saturate-[1.8] bg-white/70 dark:bg-[#0a0a0b]/60 border-b border-gray-200/30 dark:border-[#1A1A1A] lg:backdrop-blur-2xl lg:backdrop-saturate-[1.8] lg:bg-white/70 lg:dark:bg-[#0a0a0b]/50 lg:border-gray-200/20 lg:dark:border-white/[0.04] transition-[top] duration-300 lg:top-14 lg:z-[90]">
          <div className="py-2 lg:py-2.5 px-4 lg:px-6">
            <UsersFilters
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              selectedFilter={selectedFilter}
              setSelectedFilter={handleSetFilter}
              selectedMarket={selectedMarket}
              setSelectedMarket={setSelectedMarket}
              sortBy={sortBy}
              setSortBy={handleSetSort}
              resultsCount={selectedFilter === "watchlist" ? filteredTraders.length : totalTraders}
              viewMode={viewMode}
              setViewMode={setViewMode}
              timeWindowSlot={HIDE_PREDEXON ? renderTimePills(true) : undefined}
              source={effectiveSource}
              timePeriod={timePeriod}
              setTimePeriod={(tp: string) => {
                setTimePeriod(tp);
                setCurrentPage(1);
                const implicitDefault = effectiveSource === 'polymarket' ? '1d' : 'all_time';
                syncUrlParam("time", tp === implicitDefault ? null : tp);
              }}
              advancedFilterCount={advancedFilterCount}
              onOpenFilters={effectiveSource === 'polymarket' ? () => setIsFiltersOpen(true) : undefined}
              showXOnlyToggle={canToggleXFilter}
              xOnlyEnabled={xFilterEnabled}
              onToggleXOnly={handleToggleXFilter}
              watchlistCount={watchlistAddresses.length}
            />
          </div>
        </div>
      )}

      {/* PvP Battle Arena - Shows above grid when comparing */}
      <CompareTraders
        traders={compareTraders}
        onRemove={handleRemoveFromCompare}
        onClear={handleClearCompare}
        onCopy={handleCopyTrader}
      />

      {initialLoading || watchlistLoading ? (
        <TradersSkeletonLoader viewMode={viewMode} />
      ) : (
        <>
          {/* Filters Panel — expands above table */}
          {isFiltersOpen && effectiveSource === 'polymarket' && (
            <div className="mb-4">
              <AdvancedFiltersPanel
                isOpen={isFiltersOpen}
                onClose={() => setIsFiltersOpen(false)}
                filters={advancedFilters}
                onApply={(filters) => {
                  setAdvancedFilters(filters);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
          {/* Time window selector — flush above the grid/list so the user
              sees it right before scanning rows. Centered + compact pills.
              Hidden on the Favorites filter because the watchlist grid is
              client-side filtered (no server window — favs are static
              addresses, not a time-bucketed leaderboard). The selector
              would do nothing useful there. */}
          {/* Standalone time-window row. Under HIDE_PREDEXON the pills move into
              the desktop filter bar (timeWindowSlot), so this copy is desktop-
              hidden (lg:hidden) and only shows on mobile. */}
          {selectedFilter !== 'watchlist' && (
            <div className={`px-4 lg:px-6 pt-3 lg:pt-4${HIDE_PREDEXON ? ' lg:hidden' : ''}`}>
              {renderTimePills(false)}
            </div>
          )}
          {/* While refetching (time-window/sort/filter change) show the
              skeleton instead of a faded-overlay + spinner over stale data.
              Cleaner UX: user sees clearly that we're loading the new slice
              rather than half-faded old data with a small spinner. */}
          {searching || refetching ? (
            <TradersSkeletonLoader viewMode={viewMode} />
          ) : (
            <UsersGrid
              traders={filteredTraders}
              timeframeLabel={effectiveSource === 'polymarket' ? t(`traders.window.${timePeriod === 'all_time' ? 'allTime' : timePeriod}`) : undefined}
              // Under HIDE_PREDEXON a specific window (1d/7d/30d) only carries
              // free PnL+Volume — the grid drops trades/bigwin/balance for those
              // views (they don't exist per-window on the free tier). all_time
              // keeps the full column set.
              pmWindowedView={HIDE_PREDEXON && effectiveSource === 'polymarket' && timePeriod !== 'all_time'}
              watchlistAddresses={watchlistAddresses}
              onWatchlistChange={setWatchlistAddresses}
              viewMode={viewMode}
              activeFilter={selectedFilter}
              onClearFilters={() => {
                handleSetFilter("all");
                setSelectedMarket("all");
                setSearchTerm("");
              }}
              compareTraders={compareTraders}
              onAddToCompare={handleAddToCompare}
              sortBy={sortBy}
              onSort={handleSetSort}
              source={effectiveSource}
              sparklines={sparklines}
              onCopy={(trader) => setCopyModal({ isOpen: true, trader })}
              // Gate the per-row X logo by the VIEWER's permission
              // (canToggleXFilter = founder allow-list, or dev locally), NOT by
              // whether the X-only filter is toggled. An allowed viewer sees the
              // X badge next to any trader with a handle even in the normal
              // list; regular users never see it.
              showXHandleRow={canToggleXFilter}
            />
          )}

          {/* Pagination - hide for watchlist (client-side filtered) */}
          {totalPages > 1 && selectedFilter !== "watchlist" && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-xs font-sans font-semibold rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-white dark:bg-[#18181D] text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-white/[0.12] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t("traders.pager.prev")}
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-9 h-9 text-xs font-sans font-semibold rounded-[5px] transition-colors ${
                        currentPage === pageNum
                          ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
                          : "border border-gray-200 dark:border-[#1A1A1A] bg-white dark:bg-[#18181D] text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-white/[0.12]"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-xs font-sans font-semibold rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-white dark:bg-[#18181D] text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-white/[0.12] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t("traders.pager.next")}
              </button>
            </div>
          )}

          {/* Page info — now also surfaces the total trader count next
              to "Showing X-Y", moved here from the header counter pill
              (2026-05-24 user feedback). The count still swings with the
              time-window selector, but the visual context (right next to
              the pager range) makes that obvious instead of feeling like
              "the platform only has 40 traders". Hidden for Kalshi (no
              pagination). */}
          {totalTraders > 0 && selectedFilter !== "watchlist" && effectiveSource !== 'kalshi' && (() => {
            const rangeStr = `${(startIndex + 1).toLocaleString()}-${Math.min(startIndex + ITEMS_PER_PAGE, totalTraders).toLocaleString()}`;
            const totalStr = totalTraders.toLocaleString();
            const template = t("traders.pager.showingOfTraders", {
              range: "§§RANGE§§",
              total: "§§TOTAL§§",
            });
            // Split on both placeholders so the two numbers can be bold.
            const tokens = template.split(/§§(RANGE|TOTAL)§§/);
            return (
              <div className="text-center text-xs font-sans text-gray-500 dark:text-gray-400 mt-4">
                {tokens.map((part, idx) => {
                  if (part === "RANGE") {
                    return (
                      <span key={idx} className="text-gray-900 dark:text-white font-semibold">
                        {rangeStr}
                      </span>
                    );
                  }
                  if (part === "TOTAL") {
                    return (
                      <span key={idx} className="text-gray-900 dark:text-white font-semibold">
                        {totalStr}
                      </span>
                    );
                  }
                  return <span key={idx}>{part}</span>;
                })}
              </div>
            );
          })()}
        </>
      )}

      {/* Copy Trader Modal */}
      {copyModal.trader && (
        <CopyTraderModal
          isOpen={copyModal.isOpen}
          onClose={() => setCopyModal({ isOpen: false, trader: null })}
          trader={{
            username: copyModal.trader.username || `${copyModal.trader.address.slice(0, 6)}...${copyModal.trader.address.slice(-4)}`,
            wallet: copyModal.trader.address,
            avatar: copyModal.trader.avatar || "",
            portfolio: copyModal.trader.currentBalance ?? null,
          }}
        />
      )}
    </div>
  );
}
